import type { VehicleT } from "@/app/types/gebrauchtwagen";
import type { StructureT } from "@/app/types/structure";

import type {
  VehicleColorFilterValue,
  VehicleConditionFilterValue,
  VehicleTypeFilterValue,
} from "./value-types";

export function normalizeSearchTerm(value?: string): string | undefined {
  const normalized = value?.trim().toLowerCase();
  return normalized && normalized.length > 0 ? normalized : undefined;
}

function normalizeCandidate(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "number") {
    return normalizeForComparison(String(value));
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? normalizeForComparison(trimmed) : undefined;
  }

  return undefined;
}

function normalizeForComparison(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/ß/g, "ss")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\- ]+/g, " ") // Zahlen (0-9) und Bindestriche (-) hinzugefügt
    .replace(/\s+/g, " ")
    .trim();
}

export function mapColorLabelToFilterValue(
  label: string
): VehicleColorFilterValue | undefined {
  const s = normalizeForComparison(label);
  if (!s) {
    return undefined;
  }

  if (s.includes("schwarz") || s.includes("black")) return "black";

  if (
    s.includes("weiss") ||
    s.includes("weis ") ||
    s === "weis" ||
    s.includes("white")
  )
    return "white";

  if (
    s.includes("grau") ||
    s.includes("anthrazit") ||
    s.includes("anthracite") ||
    s.includes("graphit") ||
    s.includes("graphite") ||
    s.includes("platin") ||
    s.includes("platinum") ||
    s.includes("charcoal") ||
    s.includes("grey") ||
    s.includes("gray")
  )
    return "gray";

  if (s.includes("silber") || s.includes("silver") || s.includes("argent"))
    return "silver";

  if (s.includes("rot") || s.includes("red")) return "red";

  if (
    s.includes("blau") ||
    s.includes("blue") ||
    s.includes("navy") ||
    s.includes("nachtblau")
  )
    return "blue";

  if (s.includes("gelb") || s.includes("yellow")) return "yellow";

  if (
    s.includes("braun") ||
    s.includes("brown") ||
    s.includes("maron") ||
    s.includes("maroon") ||
    s.includes("cognac") ||
    s.includes("mokka") ||
    s.includes("mocha")
  )
    return "brown";

  if (
    s.includes("beige") ||
    s.includes("creme") ||
    s.includes("cream") ||
    s.includes("ivory") ||
    s.includes("sand") ||
    s.includes("stone") ||
    s.includes("champagner") ||
    s.includes("champagne")
  )
    return "beige";

  if (
    s.includes("grun") ||
    s.includes("gruen") ||
    s.includes("green") ||
    s.includes("tuerkis") ||
    s.includes("turkis") ||
    s.includes("turquoise")
  )
    return "green";

  return undefined;
}

const NEW_VEHICLE_TYPE_CODES = new Set([1]);
const USED_VEHICLE_TYPE_CODES = new Set([2]);
const NEW_EXTENDED_TYPE_CODES = new Set([1, 3, 4]);
const USED_EXTENDED_TYPE_CODES = new Set([2]);

function normalizeNumericCode(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
}

export function getVehicleCondition(
  vehicle: VehicleT
): VehicleConditionFilterValue | undefined {
  const raw = vehicle as Record<string, unknown>;

  const primaryType = normalizeNumericCode(raw.type);
  if (primaryType !== undefined) {
    if (NEW_VEHICLE_TYPE_CODES.has(primaryType)) {
      return "new";
    }
    if (USED_VEHICLE_TYPE_CODES.has(primaryType)) {
      return "used";
    }
  }

  const extendedType = normalizeNumericCode(raw.typeextendedcode);
  if (extendedType !== undefined) {
    if (NEW_EXTENDED_TYPE_CODES.has(extendedType)) {
      return "new";
    }
    if (USED_EXTENDED_TYPE_CODES.has(extendedType)) {
      return "used";
    }
  }

  const typeCode = normalizeNumericCode(raw.typecode);
  if (typeCode !== undefined) {
    if (NEW_VEHICLE_TYPE_CODES.has(typeCode)) {
      return "new";
    }
    if (USED_VEHICLE_TYPE_CODES.has(typeCode)) {
      return "used";
    }
  }

  const typeExtendedSet = raw.typeextendedset;
  if (typeExtendedSet && typeof typeExtendedSet === "object") {
    for (const [key, value] of Object.entries(typeExtendedSet)) {
      if (!/\d+/.test(key)) {
        continue;
      }
      if (!value) {
        continue;
      }
      const numericKey = Number.parseInt(key, 10);
      if (Number.isNaN(numericKey)) {
        continue;
      }
      if (NEW_EXTENDED_TYPE_CODES.has(numericKey)) {
        return "new";
      }
      if (USED_EXTENDED_TYPE_CODES.has(numericKey)) {
        return "used";
      }
    }
  }

  return undefined;
}

export function getVehicleMakeId(vehicle: VehicleT): string | undefined {
  const raw = (vehicle as Record<string, unknown>).make;

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  return undefined;
}

export function getVehicleFuelId(vehicle: VehicleT): string | undefined {
  const raw = (vehicle as Record<string, unknown>).fuel;

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  return undefined;
}

export function getVehicleTransmissionId(
  vehicle: VehicleT
): string | undefined {
  const raw = (vehicle as Record<string, unknown>).transmission;

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  return undefined;
}

export function getVehicleLocation(vehicle: VehicleT): string | undefined {
  // Use sellingdealer.city instead of location field
  const sellingDealer = (vehicle as Record<string, unknown>).sellingdealer;

  if (
    sellingDealer &&
    typeof sellingDealer === "object" &&
    sellingDealer !== null
  ) {
    const city = (sellingDealer as Record<string, unknown>).city;
    if (typeof city === "string") {
      const trimmed = city.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }
  }

  return undefined;
}

export function getVehicleNumber(vehicle: VehicleT): string | undefined {
  const raw = (vehicle as Record<string, unknown>).localcode;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  return undefined;
}

export function getVehicleModelId(vehicle: VehicleT): string | undefined {
  const raw = (vehicle as Record<string, unknown>).model;

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  return undefined;
}

export function getVehicleTypeGroup(
  vehicle: VehicleT
): VehicleTypeFilterValue | undefined {
  const raw = vehicle as Record<string, unknown>;
  const bodyCode = Number(raw.body);
  const categoryCode = Number(raw.category);
  const lengthMm = typeof raw.length === "number" ? raw.length : undefined;

  if (categoryCode === 12) return "camper";
  if (categoryCode === 11 || bodyCode === 8) return "commercial";
  if (bodyCode === 9) return "suv-pickup";
  if (bodyCode === 4 || bodyCode === 6 || bodyCode === 7 || bodyCode === 2)
    return "convertible";
  if (bodyCode === 5) return "sports-coupe";
  if (bodyCode === 14) return "small";
  if (bodyCode === 3) return "station-wagon";
  if (bodyCode === 1) return "sedan";

  if (
    categoryCode === 8 &&
    typeof lengthMm === "number" &&
    Number.isFinite(lengthMm)
  ) {
    if (lengthMm <= 4300) return "small";
  }

  return undefined;
}

export function resolveVehicleColorValue(
  vehicle: VehicleT,
  structure: StructureT
): VehicleColorFilterValue | undefined {
  const colorCode = (vehicle as Record<string, unknown>).color;
  const colorLabel = getStructureLabel(structure, "color", colorCode) ?? "";
  let mapped = mapColorLabelToFilterValue(colorLabel);

  if (!mapped) {
    const manufacturer = (vehicle as Record<string, unknown>)
      .manufacturercolor as string | undefined;
    if (manufacturer) {
      mapped = mapColorLabelToFilterValue(manufacturer);
    }
  }

  return mapped;
}

function getStructureLabel(
  structure: StructureT,
  key: keyof StructureT,
  value: unknown
): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const map = structure[key];
  if (!map) {
    return null;
  }

  const lookup = map[String(value)];
  return typeof lookup === "string" && lookup.length > 0 ? lookup : null;
}

export function vehicleMatchesSearch(
  vehicle: VehicleT,
  structure: StructureT | undefined,
  searchTerm: string
): boolean {
  const normalizedTerm = normalizeForComparison(searchTerm);
  if (!normalizedTerm) {
    return true;
  }

  const tokens = normalizedTerm.split(" ").filter(Boolean);

  const candidateValues: string[] = [];
  const vehicleRecord = vehicle as Record<string, unknown>;

  const makeId = getVehicleMakeId(vehicle);
  if (makeId) {
    candidateValues.push(normalizeForComparison(makeId));
    const makeLabel = structure?.make?.[makeId];
    if (makeLabel) {
      const normalized = normalizeCandidate(makeLabel);
      if (normalized) {
        candidateValues.push(normalized);
      }
    }
  }

  const modelId = getVehicleModelId(vehicle);
  if (modelId) {
    candidateValues.push(normalizeForComparison(modelId));
    const modelLabel = structure?.model?.[modelId];
    if (modelLabel) {
      const normalized = normalizeCandidate(modelLabel);
      if (normalized) {
        candidateValues.push(normalized);
      }
    }
  }

  const makeLabelField = normalizeCandidate(vehicleRecord.makeLabel);
  if (makeLabelField) {
    candidateValues.push(makeLabelField);
  }

  const modelLabelField = normalizeCandidate(vehicleRecord.modelLabel);
  if (modelLabelField) {
    candidateValues.push(modelLabelField);
  }

  // localCode (Fahrzeugnummer) entfernt - wird jetzt in separatem Filter behandelt

  const shortDescription = normalizeCandidate(vehicleRecord.shortdescription);
  if (shortDescription) {
    candidateValues.push(shortDescription);
  }

  if (candidateValues.length === 0) {
    return false;
  }

  const haystack = candidateValues.join(" ");

  // Strict search logic: ALL tokens must be found
  const allTokensFound = tokens.every((token) => haystack.includes(token));

  if (!allTokensFound) {
    return false;
  }

  // For multi-token searches, prefer exact phrase matches
  if (tokens.length > 1) {
    const searchPhrase = tokens.join(" ");
    if (haystack.includes(searchPhrase)) {
      return true; // Exact phrase match - highest priority
    }

    // If no exact phrase, check if tokens appear in sequence
    // This helps with cases like "audi rs 7" vs "audi rs q8"
    const haystackLower = haystack.toLowerCase();
    let lastIndex = -1;
    let sequenceMatch = true;

    for (const token of tokens) {
      const tokenIndex = haystackLower.indexOf(
        token.toLowerCase(),
        lastIndex + 1
      );
      if (tokenIndex === -1) {
        sequenceMatch = false;
        break;
      }
      lastIndex = tokenIndex;
    }

    if (sequenceMatch) {
      return true; // Tokens found in sequence
    }
  }

  // Single token or fallback: all tokens found individually
  return true;
}
